import { useMemo } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import type { PieLabelRenderProps } from 'recharts'

import ChartTooltip from '@Shared/components/ChartTooltip'
import type { PositionStatus } from '@Store/types/dashboard'

export const POSITION_SLICES: Array<{
  key: keyof PositionStatus
  name: string
  color: string
}> = [
  { key: 'highest', name: 'Highest price', color: '#F38744' },
  { key: 'higher', name: 'Higher than average', color: '#FAC515' },
  { key: 'cheaper', name: 'Cheaper than average', color: '#85E13A' },
  { key: 'cheapest', name: 'Cheapest price', color: '#22CCEE' },
  { key: 'average', name: 'Average price', color: '#A48AFB' },
  { key: 'allequal', name: 'All equal', color: '#FD6F8E' },
]

// Single source of truth for the percentages, so any legend built to sit
// alongside the chart always matches what's actually drawn.
export function getPositionSlicePercentages(
  position: PositionStatus | undefined,
): Array<{
  key: keyof PositionStatus
  name: string
  color: string
  value: number
}> {
  const sum = POSITION_SLICES.reduce(
    (total, { key }) => total + (position?.[key] ?? 0),
    0,
  )
  return POSITION_SLICES.map(({ key, name, color }) => ({
    key,
    name,
    color,
    value: sum ? Number((((position?.[key] ?? 0) / sum) * 100).toFixed(1)) : 0,
  }))
}

// Below this, a 16px bold label can't fit inside the slice without
// overlapping its neighbors — skip it and let the tooltip/legend carry
// the value instead, rather than draw illegible overlapping text.
const MIN_LABEL_PERCENT = 6

function renderPercentLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  value,
}: PieLabelRenderProps) {
  if (!value || Number(value) < MIN_LABEL_PERCENT) return null
  const RADIAN = Math.PI / 180
  const radius =
    Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) / 2
  const x = Number(cx) + radius * Math.cos(-midAngle! * RADIAN)
  const y = Number(cy) + radius * Math.sin(-midAngle! * RADIAN)

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={16}
      fontWeight={700}
    >
      {`${value}%`}
    </text>
  )
}

function PositionDoughnutChart({
  position,
  showLabels = true,
}: {
  position?: PositionStatus
  showLabels?: boolean
}): JSX.Element {
  // Stable reference: recharts' Pie treats a new `data` array as new data
  // and resets its internal hover/active-tooltip state, so an unmemoized
  // array here (recreated every render) makes the tooltip flicker/disappear
  // mid-hover whenever this component re-renders for an unrelated reason.
  const data = useMemo(() => getPositionSlicePercentages(position), [position])

  return (
    <ResponsiveContainer width="100%" aspect={1}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="45%"
          outerRadius="100%"
          startAngle={90}
          endAngle={-270}
          isAnimationActive={false}
          label={showLabels ? renderPercentLabel : false}
          labelLine={false}
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={entry.color} stroke="none" />
          ))}
        </Pie>
        <Tooltip
          content={(props) => (
            <ChartTooltip {...props} formatValue={(value) => `${value}%`} />
          )}
          wrapperStyle={{ zIndex: 50 }}
        />
      </PieChart>
    </ResponsiveContainer>
  )
}

export default PositionDoughnutChart
